import { Injectable, inject, NgZone } from '@angular/core';
import { isSupported, getMessaging, onMessage, getToken } from 'firebase/messaging';
import { initializeApp } from 'firebase/app';
import { Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { resolveFirebaseConfig, isFirebaseConfigured } from '../config/firebase.config';
import { NotificationsApiService } from './notifications-api.service';
import { NotificationService } from '../../services/notification.service';
import { notificationClickTarget } from '../../shared/utils/notification-target.util';

export interface ForegroundNotification {
    title: string;
    body: string;
    deepLink: string | null;
    data: Record<string, string>;
    receivedAt: string;
}

/**
 * PushNotificationsService — Firebase Cloud Messaging (FCM) for the web.
 *
 * Responsibilities:
 *   - Lazily initialize the Firebase web app + messaging instance
 *   - Request notification permission + get an FCM registration token
 *   - POST the token to `/API2/notifications/register-token`
 *   - Listen for foreground push messages → show a toast (re-uses the existing
 *     ngx-toastr NotificationService wrapper). Tapping the toast navigates via
 *     the deep_link from the Firebase data payload (an absolute apex URL; the
 *     origin is stripped and the path mapped to an in-SPA route).
 *   - On logout: stop the foreground listener but keep the FCM token + Service
 *     Worker alive. The token is NOT deleted — on the next login it is
 *     re-POSTed and the backend upserts/reassigns it to the new user.
 *
 * The Service Worker: when the page is in the BACKGROUND, FCM uses
 * `firebase-messaging-sw.js` (served at the app root) to display notifications.
 * We register it ourselves (Angular's built-in SW is disabled —
 * `angular.json:serviceWorker:false` and `main.ts` purges legacy registrations
 * on each load). We register ours AFTER the legacy cleanup and only when the
 * user is authenticated (no token → no SW churn).
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
    private notificationsApi = inject(NotificationsApiService);
    private notify = inject(NotificationService);
    private router = inject(Router);
    private zone = inject(NgZone);

    private app: ReturnType<typeof initializeApp> | null = null;
    private messaging: ReturnType<typeof getMessaging> | null = null;
    private unregisterForeground: (() => void) | null = null;
    private currentToken: string | null = null;
    private swRegistration: ServiceWorkerRegistration | null = null;
    private initAttempted = false;
    private swMessageListenerAttached = false;
    private audioUnlocked = false;

    /**
     * Returns the current FCM registration token, or null if not yet
     * initialized. Used by AuthEffects to pass the token to /Auth/signout
     * so the backend can detach the device on logout.
     */
    getCurrentToken(): string | null {
        return this.currentToken;
    }

    // Foreground-notification stream — emits whenever a push arrives while the
    // page is in the foreground. The bell + Notification Center subscribe to
    // this so the badge/list update instantly instead of waiting for the next
    // poll. (Background pushes go through the SW and don't emit here.)
    private onNotificationSubject = new Subject<ForegroundNotification>();
    readonly onNotification$: Observable<ForegroundNotification> = this.onNotificationSubject.asObservable();

    /**
     * Initialize Firebase + register a service worker for background push, then
     * request an FCM token and POST it to the API. Called from the
     * PushNotificationsEffects when a non-null access token arrives.
     *
     * Safe to call repeatedly — it caches the messaging instance and only
     * re-requests a token if the previous one is gone. No-op when the browser
     * is unsupported or when the Firebase web config is not yet filled in.
     */
    async initAndRegister(): Promise<void> {
        // Attach the audio-unlock listener early so the first user gesture
        // (login button click) unlocks playback for later push sounds.
        this.unlockAudio();
        if (!isFirebaseConfigured()) {
            return;
        }
        if (this.initAttempted) {
            // already initialized — restart the foreground listener (it may have
            // been stopped on logout) and re-request the token. The token is
            // re-POSTed to the API on every login; the backend upserts it
            // (ON CONFLICT DO UPDATE user_id) so the same device token is
            // reassigned to the newly-authenticated user — never shared.
            this.startForegroundListener();
            await this.ensureToken();
            return;
        }
        this.initAttempted = true;

        try {
            const supported = await isSupported();
            if (!supported) {
                return;
            }
        } catch {
            return;
        }

        try {
            const cfg = resolveFirebaseConfig();
            if (!this.app) {
                this.app = initializeApp(cfg);
            }
            if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
                try {
                    this.swRegistration = await navigator.serviceWorker.register('firebase-messaging-sw.js', { scope: './' });
                    // The SW registration is passed to getToken() below, which prefers it
                    // over autofetching — and also stops FCM from auto-registering a different
                    // SW (which would then be torn down by main.ts's legacy cleanup on the next load).
                } catch {
                    this.swRegistration = null;
                }
                this.attachSwMessageListener();
                this.messaging = getMessaging(this.app);
            } else {
                this.messaging = getMessaging(this.app);
            }

            this.startForegroundListener();
            await this.ensureToken();
        } catch (err) {
            this.initAttempted = false;
            console.warn('[PushNotifications] init failed:', err);
        }
    }

    /**
     * Request (or retrieve) the FCM token and POST it to the API. If the user
     * has already granted permission and a token exists, this is a no-op.
     */
    private async ensureToken(): Promise<void> {
        if (!this.messaging) return;
        if (typeof Notification === 'undefined') return;
        try {
            if (Notification.permission === 'default') {
                const perm = await Notification.requestPermission();
                if (perm !== 'granted') {
                    return;
                }
            } else if (Notification.permission === 'denied') {
                return;
            }

            const token = await this.zone.runOutsideAngular(() =>
                getToken(this.messaging!, {
                    serviceWorkerRegistration: this.swRegistration ?? undefined
                })
            );
            if (!token) return;
            if (token === this.currentToken) return;
            this.currentToken = token;

            this.zone.run(() => {
                this.notificationsApi
                    .registerToken({
                        token,
                        device_id: this.getDeviceId(),
                        platform: 'web',
                        device_label: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                        app_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null
                    })
                    .subscribe({
                        next: () => {},
                        error: (err) => console.warn('[PushNotifications] register-token failed:', err)
                    });
            });
        } catch (err) {
            console.warn('[PushNotifications] ensureToken failed:', err);
        }
    }

    /**
     * Listen for push messages that arrive while the page is in the FOREGROUND.
     * Background messages are handled by the Service Worker (`firebase-messaging-sw.js`).
     *
     * The FCM `data` payload includes `deep_link` (app-relative path, e.g.
     * `/offers/123`) and `title`/`body`. We show an actionable toast and only
     * navigate when the recipient chooses to open it.
     */
    private startForegroundListener(): void {
        if (!this.messaging || this.unregisterForeground) return;
        const unsub = onMessage(this.messaging, (payload) => {
            const data = (payload?.data ?? {}) as Record<string, string> || ({} as Record<string, string>);
            const title = String(data?.['title'] ?? payload?.notification?.title ?? 'Notification');
            const body = String(data?.['body'] ?? payload?.notification?.body ?? '');
            const deepLink = data?.['deep_link'] ? String(data['deep_link']) : null;

            const fg: ForegroundNotification = {
                title,
                body,
                deepLink,
                data: { ...(data || {}) },
                receivedAt: new Date().toISOString()
            };
            this.onNotificationSubject.next(fg);

            this.zone.run(() => {
                this.playNotificationSound();
                if (deepLink || data?.['type'] === 'lead_created') {
                    this.notify
                        .showActionableInfo(body || title, title, () => this.handleDeepLink(deepLink || '', data?.['type'], data))
                        .subscribe();
                } else {
                    this.notify.showInfo(body || title, title).subscribe();
                }
            });
        });
        this.unregisterForeground = () => unsub();
    }

    /**
     * Play a short notification chime when a foreground push arrives.
     *
     * Browser autoplay policy blocks audio until the user has interacted with
     * the page (clicked/tapped/keydown). We handle this two ways:
     *   1. A one-time gesture listener "unlocks" audio by playing a silent
     *      buffer on the first click/tap — after that, play() succeeds.
     *   2. We create a FRESH Audio element each call (cached elements get
     *      stuck in a "rejected" state after the first blocked play).
     */
    private playNotificationSound(): void {
        try {
            if (typeof Audio === 'undefined') return;
            const base = typeof document !== 'undefined' ? document.baseURI : '';
            const url = typeof URL !== 'undefined' && base
                ? new URL('sounds/notification.wav', base).href
                : 'sounds/notification.wav';
            const audio = new Audio(url);
            audio.volume = 0.5;
            audio.currentTime = 0;
            const p = audio.play();
            if (p && typeof p.catch === 'function') {
                p.catch(() => {
                    // Autoplay blocked — try to unlock for next time.
                    this.unlockAudio();
                });
            }
        } catch {
            // audio blocked or unavailable — silent fallback
        }
    }

    /**
     * One-time user-gesture listener that "unlocks" audio playback. Browsers
     * block Audio.play() until the user has interacted with the page. On the
     * first click/touch/keydown we play a short silent buffer so subsequent
     * play() calls (from push notifications) succeed. Safe to call multiple
     * times — the listener is only attached once.
     */
    private unlockAudio(): void {
        if (this.audioUnlocked) return;
        if (typeof document === 'undefined') return;
        const unlock = () => {
            this.audioUnlocked = true;
            // Play a silent/quiet sound to unlock the audio context.
            try {
                if (typeof Audio !== 'undefined') {
                    const base = document.baseURI || '';
                    const url = base
                        ? new URL('sounds/notification.wav', base).href
                        : 'sounds/notification.wav';
                    const a = new Audio(url);
                    a.volume = 0;
                    a.play().then(() => { a.pause(); }).catch(() => {});
                }
            } catch {}
            document.removeEventListener('click', unlock, true);
            document.removeEventListener('touchstart', unlock, true);
            document.removeEventListener('keydown', unlock, true);
        };
        document.addEventListener('click', unlock, true);
        document.addEventListener('touchstart', unlock, true);
        document.addEventListener('keydown', unlock, true);
    }

    /**
     * Deep links from the catalog are now ABSOLUTE APEX URLs of the form
     * `https://app.nibni.com/<kind>[/<id>]` (e.g. `/offer/abc`,
     * `/property/<id>`, `/team`). These apex paths are what the installed
     * mobile app's intent-filters + Universal Links match against (the
     * well-known files declare them in `components`), so a tap on a closed
     * device opens the native app. When this code runs, the SPA is already
     * loaded (foreground push click, or the browser fallback path after
     * the Public function's deeplinkRedirect handler 302-redirected to
     * `/app/n/<kind>[/<id>]`).
     *
     * `notificationClickTarget` folds every accepted shape into the in-SPA
     * neutral route `/n/<kind>[/<id>]` so the `NotificationRedirectComponent`
     * (mounted at /n/:kind and /n/:kind/:id) resolves the role-appropriate
     * destination SPA route, and applies the type-level overrides (e.g. a
     * pending offer approval opens the approvals page, not the offer). Pass
     * the notification's `type` (the FCM data payload carries it) whenever
     * the caller knows it. The `#`-fragment branch in the util is kept for
     * payloads emitted by app builds already in the field, which still carry
     * the legacy `/app/#/…` shape.
     */
    handleDeepLink(deepLink: string, type?: string | null, data?: Record<string, unknown> | null): void {
        const target = notificationClickTarget({ type, deepLink, data });
        if (!target) return;
        this.router.navigateByUrl(target, /^\/n\/(?:offer|lead)\/[^/]+$/.test(target)
            ? { onSameUrlNavigation: 'reload' } : {}).catch(() => {});
    }

    /**
     * Background-notification tap when the app is ALREADY open: the service
     * worker focuses the existing window and hands the route over via
     * postMessage (a `#…` URL would only change the fragment — the
     * path-based router would never see it). Mirrors the `nibni-deep-link`
     * message posted by `firebase-messaging-sw.js`.
     */
    private attachSwMessageListener(): void {
        if (this.swMessageListenerAttached) return;
        this.swMessageListenerAttached = true;
        navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
            const data: any = event?.data;
            if (data?.type !== 'nibni-deep-link' || !data?.path) return;
            this.zone.run(() => this.handleDeepLink(String(data.path)));
        });
    }

    /**
     * Resolve (or lazily create) a stable per-device identifier. Stored in
     * localStorage so it survives reloads. The backend uses (user_id,
     * device_id) to detect token-refresh on the same device and update the
     * existing row in place instead of accumulating stale rows.
     */
    private getDeviceId(): string {
        try {
            const KEY = 'nibni_device_id';
            let id = localStorage.getItem(KEY);
            if (!id) {
                id = (typeof crypto !== 'undefined' && crypto.randomUUID)
                    ? crypto.randomUUID()
                    : 'dev-' + Date.now() + '-' + Math.random().toString(36).slice(2);
                localStorage.setItem(KEY, id);
            }
            return id;
        } catch {
            return 'dev-' + Date.now() + '-' + Math.random().toString(36).slice(2);
        }
    }

    /**
     * Tear down the foreground listener on logout. We intentionally do NOT:
     *   - delete the FCM registration token from the server,
     *   - call `deleteToken()` on the Firebase SDK,
     *   - unregister the Service Worker.
     *
     * The same device token is reused for the next login. On re-login,
     * `initAndRegister()` re-POSTs the token to `/notifications/register-token`;
     * the backend upserts it (`ON CONFLICT (token) DO UPDATE SET user_id`),
     * so the token is reassigned to the newly-authenticated user — the same
     * token is never associated with more than one user at a time.
     *
     * We only stop the foreground listener (so the previous user's pushes
     * don't surface as toasts while logged out) and clear `currentToken`
     * (so `ensureToken()` re-registers on the next login rather than skipping).
     */
    async unregister(): Promise<void> {
        if (this.unregisterForeground) {
            try {
                this.unregisterForeground();
            } catch {}
            this.unregisterForeground = null;
        }
        this.currentToken = null;
    }
}
